/**
 * FILTROS COMPARTILHADOS entre as abas do Orquestrador (periodo + regime + empresa
 * + status).
 *
 * O estado mora na PAGINA (index.tsx), nao em cada aba: trocar de aba nunca pode
 * "esquecer" o recorte que o usuario escolheu — e a navegacao cruzada (linha do
 * Resumo → Processos filtrado) so' funciona se as duas abas leem a MESMA fonte.
 *
 * Regra de MAO UNICA (confirmada 17/08 no prototipo): escolher EMPRESA auto-preenche
 * o regime dela (contexto); escolher REGIME limpa a empresa e restringe o select de
 * empresas aquele regime. Os dois nunca atuam como dimensoes independentes.
 *
 * STATUS (pedido 04/09: "em todas as telas tem que ter um filtro de status, das
 * empresas que deram sucesso, falha, warning, executando e aguardando (fila)"):
 * multi-selecao dos 5 estados canonicos; VAZIO = sem filtro (mostra tudo); qualquer
 * subconjunto filtra em E logico com os demais filtros.
 */

import type { IEmpresaResumo, StatusEsteira } from '../api';
import type { PeriodoProcessos } from './periodoProcessos';
import { rangeDoPreset } from './periodoProcessos';

export interface FiltrosCompartilhados {
  periodo: PeriodoProcessos;
  /** Rotulo de regime (tb_empresa.regime normalizado pelo BFF) ou null = todos. */
  regime: string | null;
  /** codi_emp (= tb_empresa.external_id) ou null = todas. */
  empresa: string | null;
  /** Multi-selecao de status canonicos — VAZIO = sem filtro (mostra tudo). */
  status: StatusEsteira[];
}

/**
 * Ha' algum filtro ativo? O PERIODO nao conta (24/09).
 *
 * O periodo sempre tem valor — "30 dias" e' o default, nao uma escolha. Conta-lo
 * faria o botao "Limpar filtros" aparecer sempre aceso, e clicar nele mudaria a
 * janela de datas junto: o usuario que so' queria tirar o recorte de empresa
 * perderia o periodo que estava olhando.
 */
export const temFiltroAtivo = (f: FiltrosCompartilhados): boolean =>
  Boolean(f.regime) || Boolean(f.empresa) || f.status.length > 0;

/** Limpa regime, empresa e status — PRESERVANDO o periodo, pela mesma razao. */
export const limparFiltros = (f: FiltrosCompartilhados): FiltrosCompartilhados => ({
  ...f,
  regime: null,
  empresa: null,
  status: [],
});

/** Comando de navegacao cruzada consumido pela aba Processos (Resumo → Processos). */
export type ComandoProcessos = { tipo: 'macro' } | { tipo: 'nano'; bot: string };

export const filtrosIniciais = (): FiltrosCompartilhados => ({
  // MES ATUAL e' o default desde 25/09 (pedido do dono: "no filtro de 30 dias,
  // traga apenas mes fechado"). O `30d` que estava aqui misturava competencias —
  // no dia 25 ia de 26/08 a 25/09 e somava agosto com setembro num numero so'.
  //
  // O default de 21/09 ("30 dias, porque 7 escondia execucao") continua atendido:
  // depois do dia 7 o mes atual e' sempre mais largo que uma semana. Antes disso
  // ele e' menor de proposito — e' o que "competencia" significa.
  periodo: rangeDoPreset('mes'),
  regime: null,
  empresa: null,
  status: [],
});

/**
 * Liga/desliga um status numa lista de multi-selecao, sem mutar a recebida — a
 * MESMA regra do filtro global e do filtro local do popup (pedido 08/09).
 */
export const alternarStatusNaLista = (
  lista: StatusEsteira[],
  st: StatusEsteira,
): StatusEsteira[] => (lista.includes(st) ? lista.filter(s => s !== st) : [...lista, st]);

/** Liga/desliga um status na multi-selecao, sem mutar o estado recebido. */
export const aoAlternarStatus = (
  filtros: FiltrosCompartilhados,
  st: StatusEsteira,
): FiltrosCompartilhados => ({
  ...filtros,
  status: alternarStatusNaLista(filtros.status, st),
});

/** Nenhum status selecionado = tudo permitido; senao, so' os selecionados. */
export const statusPermitido = (selecionados: StatusEsteira[], st: StatusEsteira): boolean =>
  !selecionados.length || selecionados.includes(st);

/**
 * Recorte de STATUS sobre qualquer lista de itens com campo `st` (as listas de
 * empresa-com-status dos popups/card Fila ja' carregam o status NAQUELE contexto).
 * Filtrar AQUI — antes de agregar contagens/barras/meta — e' a regra de ouro do
 * prototipo: os numeros do no' sempre batem com o popup. Vazio = sem filtro.
 */
export const filtrarPorStatus = <T extends { st: StatusEsteira }>(
  lista: T[],
  selecionados: StatusEsteira[],
): T[] => (selecionados.length ? lista.filter(item => selecionados.includes(item.st)) : lista);

/**
 * Como `filtrarPorStatus`, mas pela MESMA régua que o nó conta (24/09).
 *
 * `filtrarPorStatus` recorta por `item.st` — o status que a dedup elegeu. O nó
 * conta por `statusEfetivo`, que corrige esse status com a ocorrência escondida
 * em outro bot do grupo (pedido de 22/09: falhou em qualquer etapa ≠ sucesso).
 *
 * Usar réguas diferentes para CONTAR e para FILTRAR é o que abria o popup vazio:
 * a pílula dizia "1 falha", o clique filtrava por `st === 'err'` e nenhuma linha
 * casava, porque o `st` dela é `done`. Contar e filtrar pela mesma régua resolve
 * os dois lados de uma vez.
 *
 * O filtro GLOBAL da página segue usando `filtrarPorStatus`: lá o recorte é por
 * bot, e o conceito de "ocorrência em outro bot do grupo" não existe.
 */
export const filtrarPorStatusEfetivo = <
  T extends { st: StatusEsteira; ocorrenciaEmOutroBot?: 'err' | 'warn' | null | undefined },
>(
  lista: T[],
  selecionados: StatusEsteira[],
): T[] =>
  selecionados.length
    ? lista.filter(item =>
        selecionados.includes(
          item.st === 'done' && item.ocorrenciaEmOutroBot ? item.ocorrenciaEmOutroBot : item.st,
        ),
      )
    : lista;

/** Escolher regime LIMPA a empresa (mao unica) — nunca deixa um par inconsistente. */
export const aoEscolherRegime = (
  filtros: FiltrosCompartilhados,
  regime: string | null,
): FiltrosCompartilhados => ({ ...filtros, regime, empresa: null });

/** Escolher empresa AUTO-PREENCHE o regime dela; limpar a empresa preserva o regime. */
export const aoEscolherEmpresa = (
  filtros: FiltrosCompartilhados,
  codiEmp: string | null,
  regimeDaEmpresa: string | null,
): FiltrosCompartilhados => {
  if (!codiEmp) return { ...filtros, empresa: null };
  return { ...filtros, empresa: codiEmp, regime: regimeDaEmpresa ?? filtros.regime };
};

/** Recorte de regime/empresa sobre o /resumo — a MESMA funcao para todas as abas. */
export const filtrarPorRegimeEmpresa = (
  empresas: IEmpresaResumo[],
  regime: string | null,
  codiEmp: string | null,
): IEmpresaResumo[] =>
  empresas.filter(
    e => (!regime || e.empresa?.regime === regime) && (!codiEmp || e.codi_emp === codiEmp),
  );

/** Valores distintos de regime, ordenados — as opcoes do select. */
export const regimesDisponiveis = (empresas: IEmpresaResumo[]): string[] =>
  Array.from(
    new Set(empresas.map(e => e.empresa?.regime).filter((r): r is string => Boolean(r))),
  ).sort((a, b) => a.localeCompare(b, 'pt-BR'));

/**
 * Opcoes do select de empresa: so' quem TEM log de bot (mesma regra do prototipo —
 * filtrar Processos por empresa sem rota nenhuma so' esvaziaria o board), restritas
 * ao regime ativo quando houver.
 */
export const empresasComLog = (
  empresas: IEmpresaResumo[],
  regime: string | null,
): IEmpresaResumo[] =>
  empresas.filter(e => e.bots.length > 0 && (!regime || e.empresa?.regime === regime));
